/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Suppression lists", description = "Exclude groups of profiles from a subscription topic without changing their subscription preferences.", lastUpdatedDate = new Date(1786579200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Suppression lists",
        description: "Exclude groups of profiles from a subscription topic without changing their subscription preferences.",
        lastUpdatedDate: "Thu Aug 13 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What you'll learn",
          "depth": 0,
          "id": "what-youll-learn"
        }, {
          "value": "When to use one",
          "depth": 0,
          "id": "when-to-use-one"
        }, {
          "value": "Create a suppression list",
          "depth": 0,
          "id": "create-a-suppression-list"
        }, {
          "value": "Choose which topics it applies to",
          "depth": 0,
          "id": "choose-which-topics-it-applies-to"
        }, {
          "value": "How suppression is enforced",
          "depth": 0,
          "id": "how-suppression-is-enforced"
        }, {
          "value": "Matching is on the profile",
          "depth": 0,
          "id": "matching-is-on-the-profile"
        }, {
          "value": "Which sends are suppressed",
          "depth": 0,
          "id": "which-sends-are-suppressed"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      h2: "h2",
      ul: "ul",
      li: "li",
      a: "a",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, lifecycle and CRM teams, marketing ops, compliance"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.strong, {
                children: "Manage settings"
              }), " permission for Native Delivery"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "A suppression list excludes a group of profiles from a subscription topic, whatever their subscription preferences say. Define who's on the list with the same audience conditions you use everywhere else, and Hightouch applies it every time a campaign builds its audience."
        })
      }), "\n", _jsx(_components.h2, {
        id: "what-youll-learn",
        children: "What you'll learn"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#when-to-use-one",
            children: "When to use a suppression list"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#create-a-suppression-list",
            children: "Creating one"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#how-suppression-is-enforced",
            children: "How they're enforced"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#which-sends-are-suppressed",
            children: "Which sends are suppressed"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "when-to-use-one",
        children: "When to use one"
      }), "\n", _jsx(_components.p, {
        children: "Use a suppression list when you want to hold back a group without touching their subscription preferences. Common cases:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Case"
            }), _jsx(_components.th, {
              children: "Example"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Regional exclusions"
              })
            }), _jsx(_components.td, {
              children: "Anyone in France or Germany, while you sort out local requirements"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Legal holds"
              })
            }), _jsx(_components.td, {
              children: "Anyone flagged by legal or compliance"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Migration overlap"
              })
            }), _jsx(_components.td, {
              children: "Profiles still receiving mail from the platform you're migrating off"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Holdouts"
              })
            }), _jsx(_components.td, {
              children: "A percentage of profiles held back so you can measure lift"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["A suppression list is the right tool when the exclusion is yours and temporary. It isn't the tool for recipients who unsubscribed or whose address bounced, both of which are already enforced on every send. See ", _jsx(_components.a, {
          href: "/native-delivery/subscriptions-and-consent",
          children: "Subscriptions and consent"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "create-a-suppression-list",
        children: "Create a suppression list"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Native Delivery → Settings → Suppression lists"
          }), " and select ", _jsx(_components.strong, {
            children: "Create"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "Name"
          }), ", and a ", _jsx(_components.strong, {
            children: "Description"
          }), " if it helps others understand the intent."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose the ", _jsx(_components.strong, {
            children: "Topics"
          }), " this list applies to. At least one is required."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Definition"
          }), " tab and build the conditions that decide who is on the list."]
        }), "\n", _jsx(_components.li, {
          children: "Save."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["A list has two tabs. ", _jsx(_components.strong, {
          children: "Definition"
        }), " holds the conditions, and ", _jsx(_components.strong, {
          children: "Subscription topics"
        }), " holds the topics it applies to. Both are editable, and changes take effect on the next send."]
      }), "\n", _jsxs(_components.p, {
        children: ["The list view shows each list's ", _jsx(_components.strong, {
          children: "Size"
        }), ", the number of profiles currently matching it. The count is cached for a day, so it can lag a definition change until it recomputes."]
      }), "\n", _jsxs(Info, {
        children: [_jsx(_components.p, {
          children: "A suppression list is a set of conditions, not an uploaded file. You build it with the same audience builder you use for audiences, and it re-evaluates against your warehouse on every send. There's no CSV import."
        }), _jsx(_components.p, {
          children: "A list with no conditions suppresses nobody, so a half-built list can't accidentally block an entire send."
        })]
      }), "\n", _jsx(_components.h2, {
        id: "choose-which-topics-it-applies-to",
        children: "Choose which topics it applies to"
      }), "\n", _jsxs(_components.p, {
        children: ["Suppression lists attach to ", _jsx(_components.a, {
          href: "/native-delivery/subscriptions-and-consent",
          children: "subscription topics"
        }), ", not to individual campaigns or messages. A campaign is suppressed by the lists attached to its topic."]
      }), "\n", _jsx(_components.p, {
        children: "One list can apply to several topics, and one topic can have several lists. Where a topic has more than one, a profile matching any of them is suppressed."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Because suppression is evaluated per topic, a list only affects campaigns sending on the topics you attach it to. A campaign on a topic with no lists attached is never suppressed. If an exclusion has to be absolute, attach the list to every topic."
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-suppression-is-enforced",
        children: "How suppression is enforced"
      }), "\n", _jsx(_components.p, {
        children: "Suppression runs in the warehouse when a campaign builds its audience, alongside subscription and bounce checks. Recipients who match are dropped before anything is sent."
      }), "\n", _jsx(_components.p, {
        children: "Two consequences worth knowing:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The reachable count already accounts for suppression."
          }), " The number you see when reviewing a campaign is the number after suppressed profiles are removed."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lists are re-evaluated as a send progresses."
          }), " A recurring campaign picks up changes to the list, and to the underlying data, on each run. There's no snapshot to refresh. For a send that spans hours, such as one pacing to recipients' local time, a profile added partway through is suppressed for the rest of it."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Suppression is checked as recipients are gathered, not at the final moment of handoff. Adding someone to a list won't recall a message already on its way out."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Suppressed recipients appear as ", _jsx(_components.strong, {
          children: "Suppressed"
        }), " in your campaign's drop reasons. See ", _jsx(_components.a, {
          href: "/native-delivery/reporting",
          children: "Reporting"
        }), ". Looking at one person, the profile shows ", _jsx(_components.strong, {
          children: "On a suppression list"
        }), " against the message they didn't receive."]
      }), "\n", _jsxs(_components.p, {
        children: ["The drop reasons report one ", _jsx(_components.strong, {
          children: "Suppressed"
        }), " total rather than a breakdown per list, so a profile on two lists counts once."]
      }), "\n", _jsx(_components.h2, {
        id: "matching-is-on-the-profile",
        children: "Matching is on the profile"
      }), "\n", _jsx(_components.p, {
        children: "A suppression list matches profiles from your parent model, not email addresses or phone numbers."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Suppressing a profile suppresses that person, not one address. If the same person appears in your warehouse as two profiles, suppressing one doesn't suppress the other. Conversely, a profile whose email address changes stays suppressed, because the match never depended on the address."
        })
      }), "\n", _jsx(_components.p, {
        children: "Because matching needs a profile, a suppression list can't exclude a bare email address that isn't in your parent model."
      }), "\n", _jsx(_components.h2, {
        id: "which-sends-are-suppressed",
        children: "Which sends are suppressed"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Send"
            }), _jsx(_components.th, {
              children: "Suppressed"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/native-delivery/campaigns",
                children: "Campaign"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/native-delivery/journeys",
                children: "Journey"
              }), " message step"]
            }), _jsx(_components.td, {
              children: "Yes"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/native-delivery/api-triggered-sends",
                children: "API-triggered"
              })
            }), _jsx(_components.td, {
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Transactional message, on any of the above"
            }), _jsx(_components.td, {
              children: "No"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "The two exemptions matter if you're using a list as a legal or compliance hold."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Transactional messages aren't suppressed."
          }), " Suppression is evaluated per subscription topic, and a transactional message has no topic, so no list applies to it. A profile on every list you have still receives transactional mail."]
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "API-triggered sends aren't suppressed."
          }), " Lists are evaluated when a campaign builds its audience from your warehouse, and an API-triggered send supplies its recipients directly, so there's no audience to filter. Exclude those recipients in your own code before you call. See ", _jsx(_components.a, {
            href: "/native-delivery/api-triggered-sends",
            children: "Send with the API"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Bounces and spam complaints are still enforced in both cases. Suppression lists and topic preferences are what get skipped."
      }), "\n", _jsx(_components.p, {
        children: "If an exclusion has to hold across every kind of send, keep the profile out of the audiences your campaigns are built on, rather than relying on a suppression list alone."
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/subscriptions-and-consent",
            children: "Subscriptions and consent"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/campaigns",
            children: "Send a campaign"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/reporting",
            children: "Reporting"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
